/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  GroupEntity,
  RELATION_CHILD_OF,
  RELATION_PARENT_OF,
  getCompoundEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { InfoCard, InfoCardVariants, Link } from '@backstage/core-components';
import {
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@material-ui/core';
import {
  EntityRefLinks,
  catalogApiRef,
  entityPresentationSnapshot,
  getEntityRelations,
  useEntity,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';
import React, { useCallback } from 'react';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';

import AccountTreeIcon from '@material-ui/icons/AccountTree';
import Alert from '@material-ui/lab/Alert';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';
import EmailIcon from '@material-ui/icons/Email';
import GroupIcon from '@material-ui/icons/Group';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import { LinksGroup } from './LinksGroup';
import { AboutContent } from '../AboutCard/AboutContent';
import { EntityCardTitle } from '../EntityCardTitle';
import { useCmdbRefresh } from '../../../hooks';
import { isNotAdGroup } from '../utils';

/** @public */
export const GroupProfileCard = (props: {
  variant?: InfoCardVariants;
  showLinks?: boolean;
}) => {
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);
  const refreshCmdb = useCmdbRefresh();
  const { entity: group } = useEntity<GroupEntity>();
  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );
  // The relations of the group are ordered by entity name, which is the CMDB
  // vuid and says nothing about the label a link is drawn with. The entities
  // themselves carry that label, so they are read and sorted by it.
  const { entities: childEntities } = useRelatedEntities(group, {
    type: RELATION_PARENT_OF,
    kind: 'Group',
  });

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(group));
      // Re-processing alone would leave the group on the data the provider
      // stored last time, so the CMDB is re-read as well.
      await refreshCmdb();
    } catch (e: any) {
      errorApi.post(e);
    }
  }, [catalogApi, refreshCmdb, errorApi, group]);

  if (!group) {
    return <Alert severity="error">Group not found</Alert>;
  }

  const {
    metadata: { name, description, title, annotations, links },
    spec: { profile },
  } = group;

  // The Entra groups a team or a department hangs off `children` are left
  // out: they are no part of the org tree, and who is in them is the Groups
  // tab's business - see isNotAdGroup. Only the entities tell them apart, so
  // until the catalog has answered there is nothing to list.
  const childRelations = childEntities
    ?.filter(isNotAdGroup)
    .sort((a, b) =>
      entityPresentationSnapshot(a).primaryTitle.localeCompare(
        entityPresentationSnapshot(b).primaryTitle,
        'en-US',
      ),
    )
    .map(getCompoundEntityRef);
  let childGroups: React.ReactNode = '';
  if (childRelations) {
    childGroups = childRelations.length ? (
      <EntityRefLinks entityRefs={childRelations} defaultKind="Group" />
    ) : (
      'N/A'
    );
  }
  const parentRelations = getEntityRelations(group, RELATION_CHILD_OF, {
    kind: 'group',
  });

  const entityLocation = annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');

  const entityMetadataEditUrl =
    group.metadata.annotations?.[ANNOTATION_EDIT_URL];

  const displayName = profile?.displayName ?? title ?? name;
  const emailHref = profile?.email ? `mailto:${profile.email}` : '#';
  const infoCardAction = entityMetadataEditUrl ? (
    <IconButton
      aria-label="Edit"
      title="Edit Metadata"
      component={Link}
      to={entityMetadataEditUrl}
    >
      <EditIcon />
    </IconButton>
  ) : (
    <IconButton aria-label="Edit" disabled title="Edit Metadata">
      <EditIcon />
    </IconButton>
  );

  return (
    <InfoCard
      title={
        // A department and a team are told apart by their icon everywhere
        // else, so the heading of the card carries it too; where no icon is to
        // be had the group one stands in, which is what every group was drawn
        // with before.
        <EntityCardTitle
          entity={group}
          title={displayName}
          fallbackIcon={GroupIcon}
        />
      }
      subheader={description}
      variant={props.variant ?? 'fullHeight'}
      action={
        <>
          {allowRefresh && canRefresh && (
            <IconButton
              aria-label="Refresh"
              title="Schedule entity refresh"
              onClick={refreshEntity}
            >
              <CachedIcon />
            </IconButton>
          )}
          {infoCardAction}
        </>
      }
    >
      {/*
        Straight into the card, not wrapped in a CardContent of its own:
        InfoCard already puts its children in one, and a second would inset
        the rows of a team further than the very same rows of a product - and
        further than the list of contacts and groups right below them here.
      */}
      <AboutContent entity={group} />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <List>
            {profile?.email && (
              <ListItem>
                <ListItemIcon>
                  <Tooltip title="Email">
                    <EmailIcon />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={<Link to={emailHref}>{profile.email}</Link>}
                  secondary="Email"
                />
              </ListItem>
            )}
            <ListItem>
              <ListItemIcon>
                <Tooltip title="Parent Group">
                  <AccountTreeIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText
                primary={
                  parentRelations.length ? (
                    <EntityRefLinks
                      entityRefs={parentRelations}
                      defaultKind="Group"
                    />
                  ) : (
                    'N/A'
                  )
                }
                secondary="Parent Group"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Tooltip title="Child Groups">
                  <GroupIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary={childGroups} secondary="Child Groups" />
            </ListItem>
            {props?.showLinks && <LinksGroup links={links} />}
          </List>
        </Grid>
      </Grid>
    </InfoCard>
  );
};
